/**
 * Lifetime companion-usage percentage (GET /assistant/usage) — the single
 * number behind the ⋯-menu usage meter and the ≥80% ambient hint. Percentage
 * only, never euros/credits/tokens (the premium "no quotas surfaced" rule).
 *
 * Returns null while loading, when the plan has no budget configured, or when
 * the fetch fails — the consuming widgets are non-critical and render nothing.
 */

import { useEffect, useState } from 'react';
import { getAssistantUsage } from 'services/api/assistant';

export function useAssistantUsage(eventId: string, refreshKey = 0): number | null {
  const [usedPct, setUsedPct] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getAssistantUsage(eventId);
        // Guard a malformed payload — a non-finite value would render "NaN%".
        const pct = data.lifetime?.usedPct;
        if (!cancelled) setUsedPct(typeof pct === 'number' && Number.isFinite(pct) ? pct : null);
      } catch {
        // Non-critical widget — stay silent and keep the last known value.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, refreshKey]);

  return usedPct == null ? null : Math.min(100, Math.max(0, Math.round(usedPct)));
}
